import type { AcceptInvitationResult } from "@/stores/invitation/invitation-store";

const FIXED_INVITE_REDIRECTS = new Set(["/account/people", "/dashboard"]);

function decodePathSegment(segment: string): string | null {
  if (!segment) {
    return null;
  }

  try {
    const decodedSegment = decodeURIComponent(segment);
    if (
      !decodedSegment ||
      decodedSegment === "." ||
      decodedSegment === ".." ||
      decodedSegment.includes("/") ||
      decodedSegment.includes("\\") ||
      /[\u0000-\u001F\u007F]/.test(decodedSegment)
    ) {
      return null;
    }

    return decodedSegment;
  } catch {
    return null;
  }
}

function buildViewerRedirectPath(projectId: string, pageId?: string): string {
  const encodedProjectId = encodeURIComponent(projectId);
  return pageId
    ? `/viewer/${encodedProjectId}/page/${encodeURIComponent(pageId)}`
    : `/viewer/${encodedProjectId}`;
}

function buildOrganizationProjectsRedirectPath(organizationSlug: string): string {
  return `/orgs/${encodeURIComponent(organizationSlug)}/projects`;
}

function getSafeInvitationRedirectPath(redirectUrl: string | undefined): string | null {
  if (typeof redirectUrl !== "string") {
    return null;
  }

  const trimmedRedirect = redirectUrl.trim();
  if (
    !trimmedRedirect.startsWith("/") ||
    trimmedRedirect.startsWith("//") ||
    trimmedRedirect.includes("\\") ||
    trimmedRedirect.includes("?") ||
    trimmedRedirect.includes("#")
  ) {
    return null;
  }

  if (FIXED_INVITE_REDIRECTS.has(trimmedRedirect)) {
    return trimmedRedirect;
  }

  const pathSegments = trimmedRedirect.split("/");

  if (pathSegments.length === 3 && pathSegments[1] === "viewer") {
    const projectId = decodePathSegment(pathSegments[2]);
    return projectId ? buildViewerRedirectPath(projectId) : null;
  }

  if (
    pathSegments.length === 5 &&
    pathSegments[1] === "viewer" &&
    pathSegments[3] === "page"
  ) {
    const projectId = decodePathSegment(pathSegments[2]);
    const pageId = decodePathSegment(pathSegments[4]);
    return projectId && pageId ? buildViewerRedirectPath(projectId, pageId) : null;
  }

  if (
    pathSegments.length === 4 &&
    pathSegments[1] === "orgs" &&
    pathSegments[3] === "projects"
  ) {
    const organizationSlug = decodePathSegment(pathSegments[2]);
    return organizationSlug
      ? buildOrganizationProjectsRedirectPath(organizationSlug)
      : null;
  }

  return null;
}

export function getInvitationAcceptRedirect(result: AcceptInvitationResult): string {
  const safeRedirectUrl = getSafeInvitationRedirectPath(result.redirectUrl);
  if (safeRedirectUrl) {
    return safeRedirectUrl;
  }

  const provisioning = result.appliedProvisioningConfig;
  if (provisioning?.homeProjectId) {
    return buildViewerRedirectPath(
      provisioning.homeProjectId,
      provisioning.homePageId ?? undefined,
    );
  }

  return result.organization?.slug
    ? buildOrganizationProjectsRedirectPath(result.organization.slug)
    : "/dashboard";
}
